/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { SectionCard } from '@/features/property-management/components/PropertyDetailView/SectionCard';
import { EntityProposalsSection } from '@/features/deals/components/EntityProposalsSection';

interface ClientProposalsTabProps {
  client: { client_id: string };
  agencyId: string;
  /** Reports the offer count so the tab strip can show it. */
  onCountChange?: (count: number) => void;
}

/**
 * Every offer this client has made, on any property. Read-only.
 *
 * Offers are made and answered inside an attendance, and this page is
 * not one: it lists every attendance's offers at once, so "accept" here
 * would have to guess which negotiation it belonged to. Recording one
 * moved out for the same reason — an offer needs the attendance around
 * it, and the client page would be opening one by the back door. The
 * attendance screen is where an offer is made and answered.
 *
 * @param props - The client, the agency, and the count callback.
 * @returns The proposals tab.
 */
export function ClientProposalsTab({ client, agencyId, onCountChange }: ClientProposalsTabProps) {
  const { t } = useTranslation('features/deals');
  const { t: tc } = useTranslation('features/clients');

  return (
    <SectionCard
      data-testid="client-proposals"
      title={t('detail.proposals.title')}
      titleAccessory={(
        <Text
          component="span"
          className="whitespace-nowrap text-[length:var(--text-meta)] text-(--text-tertiary-soft)"
        >
          {tc('detail.proposals.readOnly')}
        </Text>
      )}
    >
      <EntityProposalsSection
        agencyId={agencyId}
        clientId={client.client_id}
        onCountChange={onCountChange}
      />
    </SectionCard>
  );
}
